<!--
  ═══════════════════════════════════════════════════════════════════════════
  COMPONENTE - CAMPO DE ATRIBUTO INDIVIDUAL
  ═══════════════════════════════════════════════════════════════════════════

  Renderiza un campo individual según su tipo de atributo.
  Soporta: text, number, textarea, boolean, date, select, multiselect, etc.

  @author Sistema SISPRO ERP
  @version 1.0.0
-->

<template>
  <el-form-item
    :label="definicion.Etiqueta"
    :required="!readonly && definicion.Requerido"
    :error="!readonly ? errorMessage : undefined"
  >
    <!-- Modo readonly: mostrar valor formateado -->
    <div
      v-if="readonly"
      class="readonly-value"
    >
      {{ formattedValue }}
    </div>

    <!-- Modo editable: mostrar inputs -->
    <!-- TEXT -->
    <el-input
      v-if="
        !readonly &&
        (definicion.Tipo === 'text' ||
          definicion.Tipo === 'string' ||
          definicion.Tipo === 'email' ||
          definicion.Tipo === 'url')
      "
      :model-value="modelValue"
      :placeholder="`Ingrese ${definicion.Etiqueta}`"
      clearable
      @update:model-value="handleChange"
    />

    <!-- TEXTAREA -->
    <el-input
      v-else-if="!readonly && definicion.Tipo === 'textarea'"
      type="textarea"
      :model-value="modelValue"
      :placeholder="`Ingrese ${definicion.Etiqueta}`"
      :rows="3"
      @update:model-value="handleChange"
    />

    <!-- NUMBER -->
    <el-input-number
      v-else-if="!readonly && definicion.Tipo === 'number'"
      ref="inputRef"
      :model-value="modelValue"
      :placeholder="`Ingrese ${definicion.Etiqueta}`"
      :controls-position="'right'"
      :formatter="formatter"
      :parser="parser"
      style="width: 100%"
      @focus="handleFocus"
      @blur="handleBlur(modelValue)"
      @update:model-value="handleChange"
    />

    <!-- BOOLEAN -->
    <el-switch
      v-else-if="!readonly && definicion.Tipo === 'boolean'"
      :model-value="modelValue"
      @update:model-value="handleChange"
    />

    <!-- DATE -->
    <el-date-picker
      v-else-if="!readonly && definicion.Tipo === 'date'"
      :model-value="modelValue"
      type="date"
      placeholder="Seleccione fecha"
      format="DD/MM/YYYY"
      value-format="YYYY-MM-DD"
      style="width: 100%"
      @update:model-value="handleChange"
    />

    <!-- DATETIME -->
    <el-date-picker
      v-else-if="!readonly && definicion.Tipo === 'datetime'"
      :model-value="modelValue"
      type="datetime"
      placeholder="Seleccione fecha y hora"
      format="DD/MM/YYYY HH:mm"
      value-format="YYYY-MM-DD HH:mm:ss"
      style="width: 100%"
      @update:model-value="handleChange"
    />

    <!-- SELECT -->
    <el-select
      v-else-if="!readonly && definicion.Tipo === 'select'"
      :model-value="modelValue"
      placeholder="Seleccione una opción"
      clearable
      filterable
      style="width: 100%"
      @update:model-value="handleChange"
    >
      <el-option
        v-for="opcion in definicion.Opciones"
        :key="opcion.Clave"
        :label="opcion.Etiqueta"
        :value="opcion.Clave"
      />
    </el-select>

    <!-- MULTISELECT -->
    <el-select
      v-else-if="!readonly && definicion.Tipo === 'multiselect'"
      :model-value="modelValue"
      placeholder="Seleccione opciones"
      multiple
      clearable
      filterable
      collapse-tags
      style="width: 100%"
      @update:model-value="handleChange"
    >
      <el-option
        v-for="opcion in definicion.Opciones"
        :key="opcion.Clave"
        :label="opcion.Etiqueta"
        :value="opcion.Clave"
      />
    </el-select>

    <!-- JSON (para tipos complejos) -->
    <el-input
      v-else-if="!readonly && definicion.Tipo === 'json'"
      type="textarea"
      :model-value="jsonValueFormatted"
      placeholder="{ }"
      :rows="4"
      @update:model-value="handleJsonChange"
    />

    <!-- FALLBACK para tipos no soportados -->
    <el-input
      v-else-if="!readonly"
      :model-value="modelValue"
      :placeholder="`Tipo ${definicion.Tipo} no soportado`"
      clearable
      @update:model-value="handleChange"
    />

    <!-- Descripción adicional -->
    <template
      v-if="definicion.Descripcion"
      #extra
    >
      <span class="field-description">{{ definicion.Descripcion }}</span>
    </template>
  </el-form-item>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
} from 'vue';
import { useLocaleNumberInput } from '@/shared/composables/useLocaleNumberInput.js';

// ══════════════════════════════════════════════════════════════════════════
// PROPS & EMITS
// ══════════════════════════════════════════════════════════════════════════

const { inputRef, formatter, parser, handleFocus, handleBlur } = useLocaleNumberInput(null);

const props = defineProps({
  definicion: {
    type: Object,
    required: true,
  },
  modelValue: {
    type: [String, Number, Boolean, Array, Object, Date],
    default: null,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'validation-error']);

// ══════════════════════════════════════════════════════════════════════════
// ESTADO LOCAL
// ══════════════════════════════════════════════════════════════════════════

const errorMessage = ref('');

// ══════════════════════════════════════════════════════════════════════════
// COMPUTED
// ══════════════════════════════════════════════════════════════════════════

/**
 * Formatea el valor para visualización en modo readonly
 */
const formattedValue = computed(() => {
  if (props.modelValue === null || props.modelValue === undefined || props.modelValue === '') {
    return '—';
  }

  switch (props.definicion.Tipo) {
    case 'boolean':
      return props.modelValue ? 'Sí' : 'No';

    case 'date':
      // Formato: DD/MM/YYYY
      try {
        const date = new Date(props.modelValue);
        return date.toLocaleDateString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      } catch {
        return String(props.modelValue);
      }

    case 'datetime':
      // Formato: DD/MM/YYYY HH:mm
      try {
        const date = new Date(props.modelValue);
        return date.toLocaleString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch {
        return String(props.modelValue);
      }

    case 'number':
      // Formatear número con separadores de miles
      return Number(props.modelValue).toLocaleString(navigator.language);

    case 'select':
      // Buscar la etiqueta de la opción seleccionada
      if (props.definicion.Opciones) {
        const opcion = props.definicion.Opciones.find((opt) => opt.Clave === props.modelValue);
        return opcion ? opcion.Etiqueta : String(props.modelValue);
      }
      return String(props.modelValue);

    case 'multiselect':
      // Mostrar etiquetas de las opciones seleccionadas separadas por comas
      if (Array.isArray(props.modelValue) && props.definicion.Opciones) {
        const etiquetas = props.modelValue.map((valor) => {
          const opcion = props.definicion.Opciones.find((opt) => opt.Clave === valor);
          return opcion ? opcion.Etiqueta : valor;
        });
        return etiquetas.join(', ');
      }
      return String(props.modelValue);

    case 'json':
      // Formatear JSON con indentación
      try {
        return JSON.stringify(props.modelValue, null, 2);
      } catch {
        return String(props.modelValue);
      }

    case 'textarea':
      // Preservar saltos de línea
      return String(props.modelValue);

    default:
      // text, string, email, url, etc.
      return String(props.modelValue);
  }
});

/**
 * Formatea valor JSON para visualización
 */
const jsonValueFormatted = computed(() => {
  if (props.modelValue === null || props.modelValue === undefined) {
    return '';
  }
  try {
    return JSON.stringify(props.modelValue, null, 2);
  } catch {
    return String(props.modelValue);
  }
});

// ══════════════════════════════════════════════════════════════════════════
// MÉTODOS
// ══════════════════════════════════════════════════════════════════════════

/**
 * Maneja cambio de valor
 */
function handleChange(value) {
  errorMessage.value = '';

  // Validación inline
  const error = validateValue(value);
  if (error) {
    errorMessage.value = error;
    emit('validation-error', error);
  } else {
    emit('validation-error', null);
  }

  emit('update:modelValue', value);
}

/**
 * Maneja cambio de valor JSON
 */
function handleJsonChange(value) {
  errorMessage.value = '';

  if (!value || value.trim() === '') {
    emit('update:modelValue', null);
    emit('validation-error', null);
    return;
  }

  try {
    const parsed = JSON.parse(value);
    emit('update:modelValue', parsed);
    emit('validation-error', null);
  } catch (error) {
    errorMessage.value = 'JSON inválido';
    emit('validation-error', 'JSON inválido');
  }
}

/**
 * Valida valor según definición
 */
function validateValue(value) {
  // Requerido
  if (props.definicion.Requerido) {
    if (value === null || value === undefined || value === '') {
      return `${props.definicion.Etiqueta} es requerido`;
    }
    if (Array.isArray(value) && value.length === 0) {
      return `Debe seleccionar al menos una opción`;
    }
  }

  // Si no hay valor y no es requerido, no validar más
  if (value === null || value === undefined || value === '') {
    return null;
  }

  // Validación por tipo
  switch (props.definicion.Tipo) {
    case 'number': {
      if (isNaN(Number(value))) {
        return 'Debe ser un número válido';
      }
      break;
    }
    case 'email': {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return 'Debe ser un email válido';
      }
      break;
    }
    case 'url': {
      try {
        new URL(value);
      } catch {
        return 'Debe ser una URL válida';
      }
      break;
    }
  }

  return null;
}

// ══════════════════════════════════════════════════════════════════════════
// WATCHERS
// ══════════════════════════════════════════════════════════════════════════

watch(
  () => props.modelValue,
  (newVal) => {
    // Revalidar cuando cambia el valor externamente
    const error = validateValue(newVal);
    errorMessage.value = error || '';
  },
);
</script>

<style scoped>
.field-description {
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

.readonly-value {
  /* display: inline-flex; */
  /* align-items: center; */
  width: 100%;
  /* min-height: 32px; */
  padding: 0 12px;
  box-sizing: border-box;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background-color: #f5f7fa;
  color: #606266;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
